import { apiClient } from '@/services/api/client'
import { PLATFORM_ROLES } from '@/types/auth'
import { useAuthStore } from '@/stores/authStore'
import type {
  NotificationListResponse,
  UnreadCountResponse,
  NotificationFilters,
  UserNotification,
} from '../types'

function getBasePath(): string {
  const { user } = useAuthStore.getState()
  // Route by ACTIVE scope, not the global account role. A platform admin who has
  // switched into a tenant (user.tenantId set) must read the TENANT feed, where
  // their approval/reminder notifications are written (tenant-scoped rows). Only
  // the platform console — no active tenant — reads the platform feed. Keying off
  // role alone sent platform admins acting in a tenant to /platform, which never
  // contains their tenant notices → an empty bell.
  if (user?.tenantId) return '/tenant/user-notifications'
  // Derived, not enumerated — this used to be a 3-role literal while the
  // route guard and the panel's "View all" link used other lists, so the
  // page a user reached and the feed it fetched could disagree.
  const isPlatformConsole = !!user?.role && PLATFORM_ROLES.includes(user.role)
  return isPlatformConsole ? '/platform/user-notifications' : '/tenant/user-notifications'
}

export const notificationApi = {
  /**
   * List notifications with optional filters
   */
  list: async (filters?: NotificationFilters): Promise<NotificationListResponse> => {
    const params = new URLSearchParams()

    if (filters) {
      if (filters.status) params.append('status', filters.status)
      if (filters.type) params.append('type', filters.type)
      if (filters.limit) params.append('limit', String(filters.limit))
      if (filters.offset) params.append('offset', String(filters.offset))
    }

    const queryString = params.toString()
    const basePath = getBasePath()
    const url = queryString ? `${basePath}?${queryString}` : basePath

    return apiClient.get<NotificationListResponse>(url)
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    return apiClient.get<UnreadCountResponse>(`${getBasePath()}/unread/count`)
  },

  /**
   * Mark a single notification as read
   */
  markAsRead: async (id: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.put<{ success: boolean; message: string }>(`${getBasePath()}/${id}/read`)
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<{ success: boolean; message: string }> => {
    return apiClient.put<{ success: boolean; message: string }>(`${getBasePath()}/read/all`)
  },

  /**
   * Delete a notification
   */
  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(`${getBasePath()}/${id}`)
  },

  /**
   * Create a test notification (development only)
   */
  createTest: async (data: {
    type?: string
    title?: string
    message?: string
    action_url?: string
  }): Promise<{ success: boolean; data: UserNotification }> => {
    return apiClient.post<{ success: boolean; data: UserNotification }>(`${getBasePath()}/test`, data)
  },
}
